/**
 * String-literal unions mirroring the backend's Prisma enums and
 * `apps/backend/src/modules/api-tokens/api-token.types.ts`.
 *
 * These are deliberately vendored rather than imported from `@repo/shared-types`
 * (that package is unpublishable). `src/types/__compat__.ts` asserts they stay
 * in sync at compile time.
 */

/** Scopes an API token can carry. */
export type ApiTokenScope =
  | 'transactions:read'
  | 'bank-accounts:read'
  | 'webhooks:manage';

export const ALL_API_SCOPES: readonly ApiTokenScope[] = [
  'transactions:read',
  'bank-accounts:read',
  'webhooks:manage',
] as const;

/** `ACTIVE` = usable. `PENDING` = temporarily blocked by the owner. */
export type ApiTokenStatus = 'ACTIVE' | 'PENDING';

export type TransactionType = 'IN' | 'OUT';

export type TransactionSource = 'REAL' | 'SIMULATED';

export type BankAccountStatus =
  | 'ACTIVE'
  | 'SUSPENDED'
  | 'DELETED'
  | 'PENDING_VERIFICATION';

export type WebhookDriver = 'HTTP' | 'TELEGRAM' | 'WORDPRESS' | 'GOOGLE_SHEETS';

export type WebhookAuthType = 'NONE' | 'API_KEY' | 'HMAC';

export type WebhookScope = 'ALL' | 'SPECIFIC';

export type WebhookDeliveryStatus =
  | 'PENDING'
  | 'DELIVERED'
  | 'RETRYING'
  | 'FAILED';

/**
 * Delivery retry backoff used by the GPM Pay webhook dispatcher, in seconds.
 * Mirrors `apps/backend/src/modules/webhook-delivery/backoff.util.ts`.
 *
 * Your endpoint will be called up to {@link WEBHOOK_MAX_ATTEMPTS} times for the
 * same transaction — make it idempotent on `payload.id`.
 */
export const WEBHOOK_RETRY_SCHEDULE_SECONDS: readonly number[] = [
  10, 30, 120, 600, 3600, 21600,
] as const;

export const WEBHOOK_MAX_ATTEMPTS = 6;

/**
 * The HTTP delivery driver aborts after this long. Respond within it or the
 * delivery is recorded as failed and retried.
 */
export const WEBHOOK_DELIVERY_TIMEOUT_MS = 5000;
